import { useEffect, useState } from 'react'
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material'
import { ApiClientError, changePassword } from '../../api/client'
import { useAutoFocus } from '../../hooks/useAutoFocus'

interface ChangePasswordDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

export function ChangePasswordDialog({ open, onClose, onSuccess }: ChangePasswordDialogProps) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const contentRef = useAutoFocus<HTMLDivElement>(open)

  useEffect(() => {
    if (!open) {
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setError(null)
      setLoading(false)
    }
  }, [open])

  const handleSubmit = async () => {
    setError(null)
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match')
      return
    }
    if (newPassword.length < 8 || !/[A-Za-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      setError('Password must be at least 8 characters and include letters and numbers')
      return
    }

    setLoading(true)
    try {
      await changePassword(currentPassword, newPassword)
      onSuccess()
      onClose()
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Failed to change password')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Change Password</DialogTitle>
      <DialogContent ref={contentRef} onKeyDown={(e) => {
        if (e.key === 'Enter' && !loading) {
          e.preventDefault()
          handleSubmit()
        }
      }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          fullWidth
          type="password"
          label="Current Password"
          value={currentPassword}
          onChange={(event) => setCurrentPassword(event.target.value)}
          margin="normal"
          autoComplete="current-password"
        />
        <TextField
          fullWidth
          type="password"
          label="New Password"
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          margin="normal"
          helperText="At least 8 characters with letters and numbers"
          autoComplete="new-password"
        />
        <TextField
          fullWidth
          type="password"
          label="Confirm New Password"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          margin="normal"
          autoComplete="new-password"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Change'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
